import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Box,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Assignment as KnowledgeIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Login as LoginIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import {
  documentApi,
  knowledgeEntryApi,
  systemApi,
  Document,
  KnowledgeEntry,
  SystemStats,
  SystemFeatureStatus,
} from '../services/api';
import PageHeader from '../components/PageHeader';

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const StatTile: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  bg: string;
}> = ({ label, value, icon, color, bg }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: bg,
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" noWrap>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [recentEntries, setRecentEntries] = useState<KnowledgeEntry[]>([]);
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalEntries: 0,
    unindexedDocuments: 0,
  });
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [features, setFeatures] = useState<SystemFeatureStatus | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const userStr = localStorage.getItem('user');
      const role = userStr ? (JSON.parse(userStr).role as string) : '';
      const superAdmin = role === 'SUPER_ADMIN';
      const admin = role === 'SUPER_ADMIN' || role === 'ADMIN';
      setIsSuperAdmin(superAdmin);
      setIsAdmin(admin);

      const [allDocuments, allEntries, unindexedDocs] = await Promise.all([
        documentApi.getAllDocuments(),
        knowledgeEntryApi.getAllEntries(),
        documentApi.getUnindexedDocuments(),
      ]);

      const docList = Array.isArray(allDocuments) ? allDocuments : [];
      const entryList = Array.isArray(allEntries) ? allEntries : [];
      const unindexedList = Array.isArray(unindexedDocs) ? unindexedDocs : [];

      setDocuments(docList.slice(0, 5));
      setRecentEntries(entryList.slice(0, 5));
      setStats({
        totalDocuments: docList.length,
        totalEntries: entryList.length,
        unindexedDocuments: unindexedList.length,
      });

      try {
        setFeatures(await systemApi.getFeatures());
      } catch {
        setFeatures(null);
      }

      if (superAdmin) {
        try {
          setSystemStats(await systemApi.getStats());
        } catch {
          // 普通工作台数据已足够，监控接口失败不阻塞页面
          setSystemStats(null);
        }
      } else {
        setSystemStats(null);
      }
    } catch (err) {
      console.error('获取工作台数据失败:', err);
      setError('获取数据失败，请检查后端服务是否正常');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleIndexUnindexed = async () => {
    try {
      const unindexedDocs = await documentApi.getUnindexedDocuments();
      if (unindexedDocs.length > 0) {
        await knowledgeEntryApi.indexDocument(unindexedDocs[0].id);
        alert('索引任务已开始，请稍后查看结果');
      } else {
        alert('没有需要索引的文档');
      }
    } catch (err) {
      console.error('索引文档失败:', err);
      alert('索引文档失败，请重试');
    }
  };

  const handleGenerateEmbeddings = async () => {
    try {
      await knowledgeEntryApi.generateEmbeddings();
      alert('嵌入向量生成任务已开始，请稍后查看结果');
    } catch (err) {
      console.error('生成嵌入向量失败:', err);
      alert('生成嵌入向量失败，请重试');
    }
  };

  const exportAuditLogs = async () => {
    try {
      const logs = await systemApi.exportAuditLogs(7);
      const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '导出审计日志失败');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && documents.length === 0 && !systemStats) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const overviewStats = systemStats
    ? [
        {
          label: '总文档数',
          value: systemStats.totalDocuments,
          icon: <DocumentIcon />,
          color: '#2563EB',
          bg: 'rgba(37, 99, 235, 0.08)',
        },
        {
          label: '知识条目数',
          value: systemStats.totalKnowledgeEntries,
          icon: <KnowledgeIcon />,
          color: '#D97706',
          bg: 'rgba(217, 119, 6, 0.08)',
        },
        {
          label: '未索引文档',
          value: systemStats.unindexedDocuments,
          icon: <UploadIcon />,
          color: systemStats.unindexedDocuments > 0 ? '#DC2626' : '#059669',
          bg:
            systemStats.unindexedDocuments > 0
              ? 'rgba(220, 38, 38, 0.08)'
              : 'rgba(5, 150, 105, 0.08)',
        },
        {
          label: '用户 / 部门',
          value: `${systemStats.totalUsers} / ${systemStats.totalDepartments}`,
          icon: <PeopleIcon />,
          color: '#059669',
          bg: 'rgba(5, 150, 105, 0.08)',
        },
        {
          label: '今日问答',
          value: systemStats.todayChatMessages,
          icon: <ChatIcon />,
          color: '#2563EB',
          bg: 'rgba(37, 99, 235, 0.08)',
        },
        {
          label: '今日上传',
          value: systemStats.todayUploads,
          icon: <UploadIcon />,
          color: '#059669',
          bg: 'rgba(5, 150, 105, 0.08)',
        },
        {
          label: '今日登录',
          value: systemStats.todayLogins,
          icon: <LoginIcon />,
          color: '#7C3AED',
          bg: 'rgba(124, 58, 237, 0.08)',
        },
        {
          label: '存储占用',
          value: formatBytes(systemStats.storageBytes),
          icon: <StorageIcon />,
          color: '#4B5563',
          bg: 'rgba(75, 85, 99, 0.08)',
        },
      ]
    : [
        {
          label: '总文档数',
          value: stats.totalDocuments,
          icon: <DocumentIcon />,
          color: '#2563EB',
          bg: 'rgba(37, 99, 235, 0.08)',
        },
        {
          label: '知识条目数',
          value: stats.totalEntries,
          icon: <KnowledgeIcon />,
          color: '#6366F1',
          bg: 'rgba(99, 102, 241, 0.08)',
        },
        {
          label: '未索引文档',
          value: stats.unindexedDocuments,
          icon: <UploadIcon />,
          color: stats.unindexedDocuments > 0 ? '#DC2626' : '#059669',
          bg:
            stats.unindexedDocuments > 0
              ? 'rgba(220, 38, 38, 0.08)'
              : 'rgba(5, 150, 105, 0.08)',
        },
      ];

  return (
    <Box className="page-container">
      <PageHeader
        title="工作台"
        subtitle={
          systemStats
            ? `运行概览 · ${systemStats.healthy ? '服务正常' : '服务异常'} · v${systemStats.version}`
            : '知识库运行概览与常用快捷操作'
        }
        action={
          isSuperAdmin ? (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={exportAuditLogs}>
                导出审计日志
              </Button>
              <Button variant="contained" size="small" onClick={loadData}>
                刷新
              </Button>
            </Stack>
          ) : undefined
        }
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {features && !(features.ocrEnabled && features.ocrConfigured) && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            isAdmin ? (
              <Button color="inherit" size="small" onClick={() => navigate('/system-config')}>
                系统配置
              </Button>
            ) : undefined
          }
        >
          {features.hint}
          {!isAdmin && ' 如需开启图片文字识别，请联系管理员配置 OCR。'}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {overviewStats.map((stat) => (
          <Grid item xs={12} sm={6} md={systemStats ? 3 : 4} key={stat.label}>
            <StatTile {...stat} />
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Typography variant="h6" gutterBottom>
          快速操作
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              fullWidth
              onClick={() => navigate('/upload')}
            >
              上传文档
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SearchIcon />}
              fullWidth
              onClick={() => navigate('/search')}
            >
              搜索知识
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleIndexUnindexed}
              disabled={stats.unindexedDocuments === 0 && (!systemStats || systemStats.unindexedDocuments === 0)}
            >
              索引文档
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" color="secondary" fullWidth onClick={handleGenerateEmbeddings}>
              生成嵌入向量
            </Button>
          </Grid>
          {isAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth onClick={() => navigate('/system-config')}>
                系统配置（OCR 等）
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}
          >
            <Typography variant="h6" gutterBottom>
              最新文档
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {documents.length > 0 ? (
              documents.map((doc) => (
                <Card key={doc.id} sx={{ mb: 2 }} className="card-hover" variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{doc.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {doc.description || '无描述'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      上传于: {new Date(doc.createdAt).toLocaleString()}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate(`/documents/${doc.id}`)}>
                      查看详情
                    </Button>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Typography>暂无文档</Typography>
            )}
            {documents.length > 0 && (
              <Button variant="text" color="primary" fullWidth onClick={() => navigate('/documents')}>
                查看全部
              </Button>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}
          >
            <Typography variant="h6" gutterBottom>
              最新知识条目
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {recentEntries.length > 0 ? (
              recentEntries.map((entry) => (
                <Card key={entry.id} sx={{ mb: 2 }} className="card-hover" variant="outlined">
                  <CardContent>
                    <Typography variant="body1">
                      {entry.content.length > 100
                        ? `${entry.content.substring(0, 100)}...`
                        : entry.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      来自文档: {entry.document?.title || '-'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => navigate(`/documents/${entry.document.id}`)}
                      disabled={!entry.document?.id}
                    >
                      查看文档
                    </Button>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Typography>暂无知识条目</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
